import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FaqDisclosureItem {
  id: string;
  question: string;
  answer: ReactNode;
}

interface FaqDisclosureListProps {
  items: ReadonlyArray<FaqDisclosureItem>;
  /** Shared `<details name>`: the browser keeps at most one answer open in this group. */
  name: string;
  className?: string;
  itemClassName?: string;
  questionClassName?: string;
  chevronClassName?: string;
  answerClassName?: string;
}

/**
 * FAQ list built on native <details>/<summary>.
 *
 * Every answer stays in the HTML whether its item is open or closed, so the
 * prerendered page and search/AI crawlers receive the full answer text that the
 * FAQPage JSON-LD describes. (Radix Accordion renders no children for closed
 * items, even with forceMount, which left only the questions in the HTML.)
 */
export const FaqDisclosureList = ({
  items,
  name,
  className,
  itemClassName,
  questionClassName,
  chevronClassName,
  answerClassName,
}: FaqDisclosureListProps) => (
  <div className={cn("w-full", className)}>
    {items.map(item => (
      <details key={item.id} name={name} className={cn("group border-b", itemClassName)}>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 [&::-webkit-details-marker]:hidden">
          <h3 className={cn("m-0 text-left font-medium", questionClassName)}>{item.question}</h3>
          <ChevronDown
            aria-hidden="true"
            className={cn("h-4 w-4 shrink-0 transition-transform duration-200 group-open:rotate-180", chevronClassName)}
          />
        </summary>
        <div className={cn("pb-4", answerClassName)}>{item.answer}</div>
      </details>
    ))}
  </div>
);
